import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { 
  Calendar, 
  Link2, 
  Trash2, 
  CheckCircle2, 
  AlertCircle, 
  ExternalLink,
  RefreshCw,
  Settings,
  Key,
  Zap,
  Hand,
  Users,
  ArrowRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CalendarIntegrations({ actorId }) {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
  const [showCodeDialog, setShowCodeDialog] = useState(false);
  const [showChangeActorDialog, setShowChangeActorDialog] = useState(false);
  const [allActors, setAllActors] = useState([]);
  const [selectedNewActorId, setSelectedNewActorId] = useState("");
  const [changingActor, setChangingActor] = useState(false);
  const [authCode, setAuthCode] = useState("");
  const [exchangingCode, setExchangingCode] = useState(false);
  const [credentials, setCredentials] = useState({
    client_id: "",
    client_secret: "",
    auth_mode: "manual", // "manual" or "automatic"
    production_redirect_uri: ""
  });
  const [savingCredentials, setSavingCredentials] = useState(false);
  const [calendarConfig, setCalendarConfig] = useState(null);

  // Detect if we're in production (not localhost)
  const isProduction = !BACKEND_URL.includes('localhost');
  const defaultProductionRedirectUri = `${BACKEND_URL}/api/gcal/cb`;

  // Get the current redirect URI to display
  const getCurrentRedirectUri = () => {
    if (credentials.auth_mode === "automatic") {
      // In automatic mode, show production URL (either entered or auto-detected)
      return credentials.production_redirect_uri || defaultProductionRedirectUri;
    }
    // In manual mode, show localhost
    return "http://localhost:8001/api/gcal/cb";
  };

  useEffect(() => {
    if (actorId) {
      fetchIntegrations();
      fetchCalendarConfig();
      fetchAllActors();
    }
  }, [actorId]);

  const fetchIntegrations = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${BACKEND_URL}/api/calendar/integrations?actor_id=${actorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIntegrations(response.data.integrations || []);
    } catch (error) {
      console.error("Failed to fetch integrations:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllActors = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${BACKEND_URL}/api/ai/actors`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const actors = Array.isArray(response.data) ? response.data : (response.data.actors || []);
      setAllActors(actors);
    } catch (error) {
      console.error("Failed to fetch actors:", error);
    }
  };

  const openChangeActorDialog = () => {
    setSelectedNewActorId("");
    setShowChangeActorDialog(true);
  };

  const changeCalendarActor = async () => {
    if (!selectedNewActorId) {
      toast.error("Please select an AI Actor");
      return;
    }

    if (selectedNewActorId === actorId) {
      toast.info("Calendar is already connected to this actor");
      setShowChangeActorDialog(false);
      return;
    }

    const googleIntegration = integrations.find(i => i.provider === "google");
    if (!googleIntegration) {
      toast.error("No calendar integration found");
      return;
    }

    setChangingActor(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${BACKEND_URL}/api/calendar/integrations/${googleIntegration.id}/actor`,
        { actor_id: selectedNewActorId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message || "Calendar moved to new AI Actor!");
      setShowChangeActorDialog(false);
      
      // Refresh the integrations list
      fetchIntegrations();
    } catch (error) {
      console.error("Failed to change actor:", error);
      toast.error(error.response?.data?.detail || "Failed to change AI Actor");
    } finally {
      setChangingActor(false);
    }
  };

  const fetchCalendarConfig = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${BACKEND_URL}/api/calendar/config`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCalendarConfig(response.data);
      
      // Set auth mode from saved config, or auto-detect for production
      const savedAuthMode = response.data?.google?.auth_mode;
      const savedRedirectUri = response.data?.google?.production_redirect_uri;
      
      // If in production and no saved mode, default to automatic
      const autoMode = isProduction && !savedAuthMode ? "automatic" : (savedAuthMode || "manual");
      
      setCredentials(prev => ({
        ...prev,
        auth_mode: autoMode,
        production_redirect_uri: savedRedirectUri || (isProduction ? defaultProductionRedirectUri : "")
      }));
    } catch (error) {
      console.error("Failed to fetch calendar config:", error);
    }
  };

  const connectGoogleCalendar = async () => {
    setConnecting(true);
    try {
      const token = localStorage.getItem("token");
      const authMode = calendarConfig?.google?.auth_mode || "manual";
      
      const response = await axios.get(
        `${BACKEND_URL}/api/calendar/google/auth?actor_id=${actorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.auth_url) {
        if (authMode === "automatic") {
          // Automatic mode - redirect in same window, Google will redirect back
          window.location.href = response.data.auth_url;
        } else {
          // Manual mode - open in new tab and show code dialog
          window.open(response.data.auth_url, '_blank');
          setShowCodeDialog(true);
        }
      }
    } catch (error) {
      console.error("Failed to initiate OAuth:", error);
      const errorMsg = error.response?.data?.detail || "Failed to connect Google Calendar";
      toast.error(errorMsg);
      
      // If credentials not configured, show dialog
      if (errorMsg.includes("not configured")) {
        setShowCredentialsDialog(true);
      }
    } finally {
      setConnecting(false);
    }
  };

  const exchangeAuthCode = async () => {
    if (!authCode.trim()) {
      toast.error("Please enter the authorization code");
      return;
    }
    
    setExchangingCode(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${BACKEND_URL}/api/calendar/google/exchange-code`,
        { code: authCode.trim(), actor_id: actorId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || "Google Calendar connected!");
      setShowCodeDialog(false);
      setAuthCode("");
      fetchIntegrations();
    } catch (error) {
      console.error("Failed to exchange code:", error);
      toast.error(error.response?.data?.detail || "Failed to connect calendar");
    } finally {
      setExchangingCode(false);
    }
  };

  const testConnection = async (integrationId) => {
    setTestResult({ loading: true });
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${BACKEND_URL}/api/calendar/google/test?actor_id=${actorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTestResult(response.data);
      
      if (response.data.success) {
        toast.success("Calendar connection is working!");
      } else {
        toast.error(response.data.error || "Connection test failed");
      }
    } catch (error) {
      console.error("Test failed:", error);
      setTestResult({ success: false, error: error.response?.data?.detail || "Test failed" });
      toast.error("Connection test failed");
    }
  };

  const deleteIntegration = async (integrationId) => {
    if (!window.confirm("Are you sure you want to disconnect this calendar?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${BACKEND_URL}/api/calendar/integrations/${integrationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Calendar disconnected");
      fetchIntegrations();
    } catch (error) {
      console.error("Failed to delete integration:", error);
      toast.error("Failed to disconnect calendar");
    }
  };

  const saveCredentials = async () => {
    // If no existing credentials, both fields are required
    const hasExisting = calendarConfig?.google?.has_credentials;
    
    if (!hasExisting && (!credentials.client_id || !credentials.client_secret)) {
      toast.error("Please enter both Client ID and Client Secret");
      return;
    }
    
    // If automatic mode, production redirect URI is required
    if (credentials.auth_mode === "automatic" && !credentials.production_redirect_uri) {
      toast.error("Production Redirect URI is required for automatic mode");
      return;
    }
    
    setSavingCredentials(true);
    try {
      const token = localStorage.getItem("token");
      
      // Build data to save
      const dataToSave = {
        auth_mode: credentials.auth_mode,
        production_redirect_uri: credentials.production_redirect_uri
      };
      if (credentials.client_id) dataToSave.client_id = credentials.client_id;
      if (credentials.client_secret) dataToSave.client_secret = credentials.client_secret;
      
      await axios.post(
        `${BACKEND_URL}/api/calendar/config`,
        dataToSave,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Google Calendar configuration saved!");
      setShowCredentialsDialog(false);
      setCredentials(prev => ({ ...prev, client_id: "", client_secret: "" })); // Reset credential fields only
      fetchCalendarConfig();
    } catch (error) {
      console.error("Failed to save credentials:", error);
      toast.error(error.response?.data?.detail || "Failed to save credentials");
    } finally {
      setSavingCredentials(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
        </CardContent>
      </Card>
    );
  }

  const googleIntegration = integrations.find(i => i.provider === "google");

  return (
    <div className="space-y-6">
      {/* Google Calendar Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center border">
                <svg viewBox="0 0 24 24" className="w-8 h-8">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <div>
                <CardTitle className="text-lg">Google Calendar</CardTitle>
                <CardDescription>
                  Connect your Google Calendar for appointment scheduling
                </CardDescription>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCredentialsDialog(true)}
              className="flex items-center gap-2"
            >
              <Key className="w-4 h-4" />
              Configure API
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {googleIntegration ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-900">Connected</p>
                    <p className="text-sm text-green-700">{googleIntegration.calendar_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => testConnection(googleIntegration.id)}
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Test
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openChangeActorDialog}
                    className="flex items-center gap-2"
                  >
                    <Users className="w-4 h-4" />
                    Change Actor
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => deleteIntegration(googleIntegration.id)}
                    className="flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Disconnect
                  </Button>
                </div>
              </div>

              {/* Current Actor Info */}
              <div className="flex items-center gap-2 text-sm text-slate-600 px-1">
                <Users className="w-4 h-4" />
                <span>Linked to AI Actor:</span>
                <span className="font-medium text-slate-900">
                  {allActors.find(a => a.id === actorId)?.name || "Current Actor"}
                </span>
              </div>

              {/* Test Result */}
              {testResult && !testResult.loading && (
                <div className={`p-4 rounded-lg border ${testResult.success ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'}`}>
                  {testResult.success ? (
                    <div>
                      <p className="font-medium text-blue-900 flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" />
                        {testResult.message}
                      </p>
                      {testResult.upcoming_events_count > 0 && (
                        <div className="mt-2 text-sm text-blue-700">
                          <p className="font-medium">Upcoming Events ({testResult.upcoming_events_count}):</p>
                          <ul className="mt-1 space-y-1">
                            {testResult.upcoming_events?.map((event, idx) => (
                              <li key={idx}>• {event.summary} - {new Date(event.start).toLocaleDateString()}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-red-900 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {testResult.error}
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6">
              <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 mb-4">
                Connect your Google Calendar to enable AI-powered appointment booking
              </p>
              <Button
                onClick={connectGoogleCalendar}
                disabled={connecting}
                className="flex items-center gap-2"
              >
                {connecting ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Link2 className="w-4 h-4" />
                )}
                Connect Google Calendar
              </Button>
              
              {calendarConfig && !calendarConfig.configured && (
                <p className="mt-4 text-sm text-amber-600 flex items-center justify-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Google API credentials not configured. Click "Configure API" above.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Setup Instructions</CardTitle>
        </CardHeader>
        <CardContent className="prose prose-sm max-w-none">
          <ol className="list-decimal list-inside space-y-2 text-slate-600">
            <li>Go to <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline inline-flex items-center gap-1">Google Cloud Console <ExternalLink className="w-3 h-3" /></a></li>
            <li>Create a new project or select an existing one</li>
            <li>Enable "Google Calendar API" in APIs & Services → Library</li>
            <li>Go to APIs & Services → Credentials</li>
            <li>Click "Create Credentials" → "OAuth client ID"</li>
            <li>Application type: "Web application"</li>
            <li>Add this Authorized redirect URI:
              <code className={`block mt-1 p-2 rounded text-xs break-all ${isProduction ? "bg-green-100 text-green-700" : "bg-slate-100"}`}>
                {isProduction ? defaultProductionRedirectUri : "http://localhost:8001/api/gcal/cb"}
              </code>
              {isProduction && (
                <span className="text-xs text-green-600 mt-1 block">✓ Production URL auto-detected</span>
              )}
            </li>
            <li>Copy the Client ID and Client Secret</li>
            <li>Click "Configure API" above and paste your credentials</li>
            <li>Click "Connect Google Calendar" to authorize</li>
          </ol>
        </CardContent>
      </Card>

      {/* Credentials Dialog */}
      <Dialog open={showCredentialsDialog} onOpenChange={setShowCredentialsDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Google Calendar API Configuration
            </DialogTitle>
            <DialogDescription>
              Configure your Google OAuth credentials and authorization mode.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Authorization Mode Toggle */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-medium">Authorization Mode</Label>
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${credentials.auth_mode === 'manual' ? 'text-indigo-600 font-medium' : 'text-slate-400'}`}>
                    Manual
                  </span>
                  <Switch
                    checked={credentials.auth_mode === "automatic"}
                    onCheckedChange={(checked) => setCredentials({ 
                      ...credentials, 
                      auth_mode: checked ? "automatic" : "manual" 
                    })}
                  />
                  <span className={`text-sm ${credentials.auth_mode === 'automatic' ? 'text-indigo-600 font-medium' : 'text-slate-400'}`}>
                    Automatic
                  </span>
                </div>
              </div>
              
              {credentials.auth_mode === "manual" ? (
                <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 rounded-lg p-3">
                  <Hand className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Manual Mode (Testing)</p>
                    <p className="text-amber-600">You'll copy the authorization code from the URL after Google login. Use this when your redirect URL can't reach the server.</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2 text-sm text-green-700 bg-green-50 rounded-lg p-3">
                  <Zap className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Automatic Mode (Production)</p>
                    <p className="text-green-600">Google will redirect back to your app automatically. Use this when you have your own verified domain.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Production Redirect URI - Only shown in Automatic mode */}
            {credentials.auth_mode === "automatic" && (
              <div className="space-y-2">
                <Label htmlFor="production_redirect_uri">Production Redirect URI *</Label>
                <Input
                  id="production_redirect_uri"
                  value={credentials.production_redirect_uri}
                  onChange={(e) => setCredentials({ ...credentials, production_redirect_uri: e.target.value })}
                  placeholder="https://yourdomain.com/api/gcal/cb"
                />
                <p className="text-xs text-slate-500">
                  Enter your production URL where Google should redirect after authorization.
                  Make sure this URL is added to your Google Console as an Authorized redirect URI.
                </p>
              </div>
            )}

            <hr className="border-slate-200" />

            {/* Client ID */}
            <div className="space-y-2">
              <Label htmlFor="client_id">Client ID</Label>
              <Input
                id="client_id"
                value={credentials.client_id}
                onChange={(e) => setCredentials({ ...credentials, client_id: e.target.value })}
                placeholder={calendarConfig?.google?.client_id_preview || "xxxx.apps.googleusercontent.com"}
              />
              {calendarConfig?.google?.has_credentials && !credentials.client_id && (
                <p className="text-xs text-green-600">✓ Saved: {calendarConfig.google.client_id_preview}</p>
              )}
            </div>

            {/* Client Secret */}
            <div className="space-y-2">
              <Label htmlFor="client_secret">Client Secret</Label>
              <Input
                id="client_secret"
                type="password"
                value={credentials.client_secret}
                onChange={(e) => setCredentials({ ...credentials, client_secret: e.target.value })}
                placeholder={calendarConfig?.google?.has_credentials ? "Leave blank to keep existing" : "Your client secret"}
              />
              {calendarConfig?.google?.has_credentials && !credentials.client_secret && (
                <p className="text-xs text-green-600">✓ Saved: {calendarConfig.google.client_secret_preview}</p>
              )}
            </div>
            
            {calendarConfig?.google?.has_credentials && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
                <p className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Credentials already configured. Enter new values to update.
                </p>
              </div>
            )}
            
            <div className={`text-sm p-3 rounded-lg ${credentials.auth_mode === "automatic" ? "bg-green-50 border border-green-200" : "bg-slate-50"}`}>
              <p className="font-medium mb-1">Redirect URI to add in Google Console:</p>
              <code className={`text-xs break-all ${credentials.auth_mode === "automatic" ? "text-green-700" : "text-slate-600"}`}>
                {getCurrentRedirectUri()}
              </code>
              {credentials.auth_mode === "automatic" && (
                <p className="text-xs text-green-600 mt-2">
                  ✓ This URL will be used for automatic OAuth redirect
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCredentialsDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveCredentials} disabled={savingCredentials}>
              {savingCredentials ? "Saving..." : (calendarConfig?.google?.has_credentials ? "Update Credentials" : "Save Credentials")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Authorization Code Dialog */}
      <Dialog open={showCodeDialog} onOpenChange={setShowCodeDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Enter Authorization Code
            </DialogTitle>
            <DialogDescription>
              A new tab opened with Google login. After approving access, you'll see an error page - that's expected!
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
              <p className="font-medium text-amber-800 mb-2">📋 How to get the code:</p>
              <ol className="list-decimal list-inside space-y-1 text-amber-700">
                <li>Log into Google in the new tab</li>
                <li>Approve calendar access</li>
                <li>You'll see a "localhost refused to connect" error</li>
                <li>Look at the <strong>URL in your browser's address bar</strong></li>
                <li>Find <code className="bg-amber-100 px-1 rounded">code=</code> and copy everything after it until the <code className="bg-amber-100 px-1 rounded">&</code></li>
              </ol>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="auth_code">Authorization Code</Label>
              <Input
                id="auth_code"
                value={authCode}
                onChange={(e) => setAuthCode(e.target.value)}
                placeholder="Paste the code from the URL here"
                className="font-mono text-sm"
              />
              <p className="text-xs text-slate-500">
                Example URL: http://localhost:8001/api/gcal/cb?code=<strong>4/0AXXXXXX...</strong>&scope=...
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCodeDialog(false); setAuthCode(""); }}>
              Cancel
            </Button>
            <Button onClick={exchangeAuthCode} disabled={exchangingCode || !authCode.trim()}>
              {exchangingCode ? "Connecting..." : "Connect Calendar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Actor Dialog */}
      <Dialog open={showChangeActorDialog} onOpenChange={setShowChangeActorDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Change AI Actor
            </DialogTitle>
            <DialogDescription>
              Move this calendar connection to a different AI Actor. This will not affect existing appointments.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Current Actor */}
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-sm text-slate-500 mb-1">Currently connected to:</p>
              <p className="font-medium text-slate-900">
                {allActors.find(a => a.id === actorId)?.name || "Current Actor"}
              </p>
            </div>

            {/* Arrow */}
            <div className="flex justify-center">
              <ArrowRight className="w-5 h-5 text-slate-400 rotate-90" />
            </div>

            {/* Select New Actor */}
            <div className="space-y-2">
              <Label>Select new AI Actor:</Label>
              <select
                value={selectedNewActorId}
                onChange={(e) => setSelectedNewActorId(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Select an AI Actor...</option>
                {allActors
                  .filter(actor => actor.id !== actorId) // Exclude current actor
                  .map((actor) => (
                    <option key={actor.id} value={actor.id}>
                      {actor.name}
                    </option>
                  ))
                }
              </select>
            </div>

            {selectedNewActorId && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                <p className="text-amber-800">
                  <strong>Note:</strong> After changing, you'll need to select the new AI Actor from the dropdown above to see this calendar connection.
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangeActorDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={changeCalendarActor} 
              disabled={changingActor || !selectedNewActorId}
            >
              {changingActor ? "Moving..." : "Confirm Change"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
